import React, { useRef } from 'react'
import Skeleton from 'react-loading-skeleton'

import 'react-loading-skeleton/dist/skeleton.css'
import { Grid, Paper, Typography, Button, Box, Tooltip } from '@material-ui/core'
import { DataGrid, GridColDef, GridColumnHeaderParams } from '@material-ui/data-grid'
import AddIcon from '@material-ui/icons/Add'
import Swal from 'sweetalert2'

import SearchDataGrid from '~/components/search-data-grid'
import { GRID_DEFAULT_LOCALE_TEXT } from '~/constants/translate-data-grid'
import { CultureCodesEnums, TypeSafraEmuns } from '~/enums'
import { useFilterValidationStateAssistant } from '~/store/validation/filter-validation-assistant'
import { useValidationStore } from '~/store/validation/validation-store'
import { IAssistantIteraction } from '~/types/validation-types'

import ModalAssistant from './components'
import string from './strings'
import { useStyles } from './styles'

export const InteractionAssistant: React.FC = () => {
  const { items } = useFilterValidationStateAssistant()
  const setFilter = useFilterValidationStateAssistant((state) => state.setFilter)
  const classes = useStyles()
  const modalAssistantRef = useRef<IAssistantIteraction>(null)

  const { assistantInteraction, SafraField, isLoading } = useValidationStore()

  const handleModalAssistant = () => {
    if (SafraField.some((item) => item.cultura_id === '0')) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção',
        text: 'Selecione uma cultura, safra e tipo safra!',
        confirmButtonColor: '#2b78c0',
        confirmButtonText: 'Ok',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showCloseButton: true,
        customClass: {
          container: 'alert-container',
        },
      })
    } else {
      modalAssistantRef.current?.handleAction()
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'usuario_criacao',
      headerName: 'Usuario',
      width: 200,
      minWidth: 120,
      valueGetter: (params: any) =>
        params.row?.usuario_criacao ? params.row.usuario_criacao : 'Usuario não informado',
      renderCell: (params: any) =>
        params.row?.usuario_criacao ? params.row.usuario_criacao : 'Usuario não informado',
      renderHeader: (params: GridColumnHeaderParams) => (
        <SearchDataGrid params={params} setFilter={setFilter} items={items} />
      ),
    },
    {
      field: 'cultura_id',
      headerName: 'Cultura',
      width: 200,
      valueGetter: (params: any) => CultureCodesEnums[params.row.cultura_id],
      renderCell: (params: any) => CultureCodesEnums[params.row.cultura_id],
      renderHeader: (params: GridColumnHeaderParams) => (
        <SearchDataGrid params={params} setFilter={setFilter} items={items} />
      ),
    },
    {
      field: 'safra',
      headerName: 'Safra',
      width: 200,
      renderHeader: (params: GridColumnHeaderParams) => (
        <SearchDataGrid params={params} setFilter={setFilter} items={items} />
      ),
      valueGetter: (params: any) => (params.row?.safra ? params.row.safra : ' - '),
      renderCell: (params: any) => (params.row?.safra ? params.row.safra : ' - '),
    },
    {
      field: 'tipo_safra',
      headerName: 'Tipo Safra',
      width: 200,
      valueGetter: (params) => TypeSafraEmuns[params.row?.tipo_safra],
      renderCell: (params: any) => {
        return TypeSafraEmuns[params.row?.tipo_safra] ?? ''
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <SearchDataGrid params={params} setFilter={setFilter} items={items} />
      ),
    },
    {
      field: 'tipo_fonte',
      headerName: 'Tipo Fonte',
      width: 200,
      renderHeader: (params: GridColumnHeaderParams) => (
        <SearchDataGrid params={params} setFilter={setFilter} items={items} />
      ),
      valueGetter: (params: any) => (params.row?.tipo_fonte ? params.row.tipo_fonte : ' - '),
      renderCell: (params: any) => (params.row?.tipo_fonte ? params.row.tipo_fonte : ' - '),
    },
    {
      field: 'nome',
      headerName: 'Nome',
      width: 200,

      renderHeader: (params: GridColumnHeaderParams) => (
        <SearchDataGrid params={params} setFilter={setFilter} items={items} />
      ),
      valueGetter: (params: any) => (params.row?.nome ? params.row.nome : ' - '),
      renderCell: (params: any) => (params.row?.nome ? params.row.nome : ' - '),
    },
    {
      field: 'meio_interacao',
      headerName: 'Meio de Interação',
      width: 200,
      renderHeader: (params: GridColumnHeaderParams) => (
        <SearchDataGrid params={params} setFilter={setFilter} items={items} />
      ),
      valueGetter: (params: any) =>
        params.row?.meio_interacao ? params.row.meio_interacao : ' - ',
      renderCell: (params: any) => (params.row?.meio_interacao ? params.row.meio_interacao : ' - '),
    },
    {
      field: 'data_interacao',
      headerName: 'Data',
      width: 200,
      valueGetter: (params) => {
        const date = new Date(params.row?.data_interacao)
        const parseDate = new Intl.DateTimeFormat('pt-br').format(date)
        return parseDate
      },
      renderCell: (params) => {
        const date = new Date(params.row?.data_interacao)
        const parseDate = new Intl.DateTimeFormat('pt-br').format(date)
        return parseDate
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <SearchDataGrid params={params} setFilter={setFilter} items={items} />
      ),
    },
    {
      field: 'observacao',
      headerName: 'Observação',
      minWidth: 500,
      flex: 1,
      valueGetter: (params: any) => (params.row?.observacao ? params.row.observacao : ' - '),
      renderCell: (params: any) => {
        const texto = params.row?.observacao ? params.row.observacao : ' - '

        return (
          <Tooltip title={texto}>
            <span className={`MuiDataGrid-cell ${classes.dataGrid} cellTolltip`}>{texto}</span>
          </Tooltip>
        )
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <SearchDataGrid params={params} setFilter={setFilter} items={items} />
      ),
    },
  ]
  return (
    <Paper variant="outlined" className={`${classes.paper}`} style={{ overflow: 'hidden' }}>
      {isLoading ? (
        <Skeleton height={300} borderRadius={10} />
      ) : (
        <>
          <Grid item container sm={12} xs={12} className={classes.headerContainer}>
            <Typography variant="h5" className={classes.typography}>
              {string.title}
            </Typography>
            <Box>
              <Button
                variant="contained"
                className={classes.buttonAdd}
                onClick={handleModalAssistant}
              >
                <AddIcon />
              </Button>
            </Box>
          </Grid>
          <DataGrid
            className={classes.dataGrid}
            rows={assistantInteraction}
            columns={columns}
            disableSelectionOnClick
            autoHeight
            filterModel={{ items }}
            onFilterModelChange={(model) => setFilter(model)}
            localeText={GRID_DEFAULT_LOCALE_TEXT}
          />
        </>
      )}

      <ModalAssistant ref={modalAssistantRef} />
    </Paper>
  )
}
